import NextImage from 'next/image';
import { useTranslation } from 'next-i18next';

import {
    Box,
    Button,
    Flex,
    Heading,
    Image,
    Link,
    Text,
    useSlotRecipe,
    VStack,
} from '@chakra-ui/react';

import { urlJoin } from 'url-join-ts';

import { urlHelpers, urlMap } from '@nocowanie/core';
import { IconsLinear } from '@nocowanie/icons';

import { environment } from '@app/environments/environment';

import { SupportProps } from './support.props';

export const Support = ({ ...props }: SupportProps) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'support',
    });

    const touristInfoItems = {
        userType: t('iAmTourist'),
        email: 'info@nocowanie.pl',
        phoneNumber: '22 116 82 96',
        openingDays: t('openingDays'),
        openingHours: '8:00 - 16:00',
    };

    const recipe = useSlotRecipe({ key: 'support' });
    const styles = recipe();

    const bgSrc = urlJoin('/', environment.ASSETS_PATH, 'svg', 'bg-support-2.svg');

    return (
        <Box css={styles.bgWrapper} {...props}>
            <Flex css={styles.container}>
                <Flex css={styles.contactInfoBox}>
                    <Heading size={'md'} mb={6}>
                        {t('supportHeader')}
                    </Heading>
                    <Text
                        hideBelow={'md'}
                        dangerouslySetInnerHTML={{ __html: `${t('supportSubHeader')}` }}
                        css={styles.subHeader}
                    />
                    <Flex css={styles.userTypesWrapper}>
                        <VStack css={styles.userTypeBox} alignItems={'start'}>
                            <Text
                                mb={2}
                                fontSize={'lg'}
                                dangerouslySetInnerHTML={{ __html: t('iAmHost') }}
                            />
                            <Button asChild colorPalette={'secondary'}>
                                <a target="_blank" href={urlMap.partnerContactForm}>
                                    {t('fillInForm')}
                                </a>
                            </Button>
                        </VStack>
                        <VStack
                            css={styles.userTypeBox}
                            alignItems={'start'}
                            justifyContent={'space-between'}
                        >
                            <Text
                                mb={2}
                                fontSize={'lg'}
                                dangerouslySetInnerHTML={{ __html: touristInfoItems.userType }}
                            />
                            <Flex alignItems={'center'} gap={2} mb={2}>
                                <IconsLinear.SmsTracking fontSize={'xl'} />
                                <Link
                                    href={`mailto:${touristInfoItems.email}`}
                                    css={styles.emailLink}
                                    variant={'plain'}
                                >
                                    {touristInfoItems.email}
                                </Link>
                            </Flex>
                            <Flex direction={'column'}>
                                <Flex gap={2} alignItems={'center'}>
                                    <IconsLinear.Call2 fontSize={'xl'} />
                                    <Link
                                        href={urlHelpers.getPhoneNoHref(
                                            touristInfoItems.phoneNumber,
                                        )}
                                        variant={'plain'}
                                        colorPalette={'gray'}
                                        fontSize={'md'}
                                    >
                                        {touristInfoItems.phoneNumber}
                                    </Link>
                                </Flex>
                                <Text css={styles.hotlineAvailability}>
                                    {touristInfoItems.openingDays}, {touristInfoItems.openingHours}
                                </Text>
                            </Flex>
                        </VStack>
                    </Flex>
                </Flex>
                <Flex css={styles.checkOurOfferBox}>
                    <Image asChild css={styles.image} fit={'contain'} alt="">
                        <NextImage alt="" src={bgSrc} width={280} height={156} />
                    </Image>
                    <Text
                        dangerouslySetInnerHTML={{ __html: `${t('addAccommodation')}` }}
                        fontWeight={'600'}
                        fontSize={'xl'}
                    />
                    <Button asChild css={styles.button} variant={'solid'}>
                        <a target="_blank" href={`/${urlMap.join}`}>
                            {t('checkOurOffer')}
                        </a>
                    </Button>
                </Flex>
            </Flex>
        </Box>
    );
};
